import Box from '@mui/material/Box';
import { useSession } from 'next-auth/react';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { QueryLoadingStatus } from '@/components/query-boundary';
import { exportDynamic } from '@/utils/export';

/**
 * Renders children only once the session has resolved, so pages and hooks
 * (`useUserPermissions`, `useCurrentUser`) never see a loading session.
 * Unauthenticated users pass through — the sign-in page relies on that status,
 * and the middleware already redirects protected routes without a token.
 */
const SessionGateContent = ({ children }: PropsWithChildren) => {
    const { status, data: session } = useSession();
    const { t } = useTranslation();

    if (status === 'loading' && !session) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                data-testid='session-gate-loading'
            >
                <QueryLoadingStatus
                    message={t('session_gate.logging_in')}
                    testid='session-gate-loading'
                />
            </Box>
        );
    }

    return <>{children}</>;
};

// exported dynamically so it is rendered on client not server
export const SessionGate = exportDynamic(SessionGateContent);
